import React, {useState} from "react";
import {useSnackbar} from "notistack";
import {apiV2} from "api/firebase";
import {copyToClipboardAsync} from "tools/common/copyToClipboardAsync";
import CloseSnackbarButton from "view/components/Snackbar/notistack/CloseButton";
import {useTaskFileShare} from "view/context/TaskFileShareContext";

/**
 * Credentials some surfaces append to a file URL for their own reads: the
 * Firebase id token (`convertFileUrl`) and the anonymous reviewer's magic-link
 * key (`withReviewKey`). A copied link must never carry either — the key in
 * particular is the reviewer's whole authentication.
 */
const CREDENTIAL_PARAMS = ["key", "token"];

const toShareableUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    CREDENTIAL_PARAMS.forEach((param) => parsed.searchParams.delete(param));
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * "Copy file link" for a task file.
 *
 * In the authenticated console (`publish` mode) every file the drawer renders
 * is uploaded `public: false`, so its baked `files.url` only resolves for a
 * caller holding a Firebase token of the show — a plain copy hands out a link
 * that 401s. `shareTaskFile` flips `files.public` (idempotent, one-way), after
 * which the same URL works for anyone. The clipboard is only written once that
 * call succeeds, so a failure never leaves a dead link behind.
 *
 * On the anonymous email-review page (`public` mode) `email-review/verify` has
 * already published everything on the page, so the URL is copied directly —
 * that surface has no session to call the share endpoint with.
 *
 * `canShare` is false outside a `TaskFileShareProvider`.
 */
export const useCopyTaskFileLink = () => {
  const share = useTaskFileShare();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const closeAction = (id: string | number) => (
    <CloseSnackbarButton title="GOT IT" onClick={() => closeSnackbar(id)} />
  );

  const copyFileLink = async ({
    fileId,
    url,
  }: {
    fileId?: string;
    url?: string;
  }): Promise<boolean> => {
    if (!share || !url || isLoading) {
      return false;
    }
    if (share.mode === "publish" && (!fileId || !share.showId)) {
      return false;
    }
    setIsLoading(true);
    try {
      const copied = await copyToClipboardAsync(async () => {
        let link = url;
        if (share.mode === "publish") {
          const {url: publicUrl} = await apiV2.task.shareTaskFile(
            {fileId: fileId as string},
            {showId: share.showId as string}
          );
          link = publicUrl || url;
        }
        return toShareableUrl(link);
      });
      enqueueSnackbar(copied ? "URL copied" : "Could not copy URL", {
        variant: copied ? "success" : "error",
        action: closeAction,
      });
      return copied;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    copyFileLink,
    isLoading,
    canShare: !!share,
    /** `publish` mode can't copy a chip that carries no file id. */
    needsFileId: share?.mode === "publish",
  };
};
